import React, { useMemo, useState } from "react";
import { Button, Descriptions, Divider, Modal, message } from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  FileZipOutlined,
  QrcodeOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { QRCodeCanvas } from "qrcode.react";
import { createPdfDownloadName } from "../pages/helpers";
import { downloadAuthenticatedFile } from "../utils/downloadFile";

const OfficePdfResultActions = ({
  downloadUrl,
  originalFileName,
  outputType = "pdf",
  requiresAuth = false,
  stacked = false,
  className = "",
  fileTypeLabel,
  fileTypeIcon,
  displayFileName,
}) => {
  const [showQrModal, setShowQrModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isZip = outputType === "zip";
  const typeLabel = fileTypeLabel || (isZip ? "ZIP" : "PDF");
  const typeIcon = fileTypeIcon || (isZip ? <FileZipOutlined /> : <DownloadOutlined />);
  const resultFileName = useMemo(
    () => {
      if (displayFileName) {
        return displayFileName;
      }

      if (isZip) {
        return originalFileName || "batch_converted_pdfs.zip";
      }

      return createPdfDownloadName(originalFileName);
    },
    [displayFileName, isZip, originalFileName]
  );
  const qrElementId = useMemo(
    () => `office-file-qr-${resultFileName.replace(/[^a-z0-9_-]/gi, "-")}`,
    [resultFileName]
  );

  if (!downloadUrl) {
    return null;
  }

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadAuthenticatedFile(downloadUrl, {
        token: requiresAuth ? localStorage.getItem("token") || "" : "",
        fileName: resultFileName,
      });
    } catch {
      message.error(`Unable to download ${typeLabel}. Please try again.`);
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: resultFileName,
          text: `Open this ${typeLabel} file`,
          url: downloadUrl,
        });
        return;
      }

      await navigator.clipboard.writeText(downloadUrl);
      message.success("Public file link copied.");
    } catch (error) {
      if (error?.name !== "AbortError") {
        message.error("Unable to share the file link.");
      }
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById(qrElementId);
    if (!canvas) {
      return;
    }

    const pngUrl = canvas.toDataURL("image/png");
    const anchor = document.createElement("a");
    anchor.download = `${resultFileName.replace(/\.[^.]+$/i, "")}-qr.png`;
    anchor.href = pngUrl;
    anchor.click();
  };

  return (
    <>
      <div
        className={`office-result-actions ${stacked ? "stacked" : ""} ${className}`.trim()}
      >
        <Button
          type="primary"
          className="convert-btn"
          icon={typeIcon}
          onClick={handleDownload}
          loading={downloading}
        >
          Download {typeLabel}
        </Button>
        <Button
          className="office-result-icon-btn"
          icon={<EyeOutlined />}
          onClick={() => window.open(downloadUrl, "_blank")}
          title={`View ${typeLabel}`}
        />
        <Button
          className="office-result-icon-btn"
          icon={<QrcodeOutlined />}
          onClick={() => setShowQrModal(true)}
          title="Generate QR"
        />
        <Button
          className="office-result-icon-btn"
          icon={<ShareAltOutlined />}
          onClick={handleShare}
          title="Share link"
        />
      </div>

      <Modal
        open={showQrModal}
        onCancel={() => setShowQrModal(false)}
        footer={null}
        width={500}
        title={<span className="office-qr-modal-title">{resultFileName}</span>}
        centered
      >
        <div className="office-qr-modal-body">
          <div className="office-qr-preview">
            <QRCodeCanvas
              id={qrElementId}
              value={downloadUrl}
              size={220}
              fgColor="#1a1a2e"
              bgColor="#ffffff"
              level="H"
              includeMargin={true}
            />
          </div>
          <Divider />
          <Descriptions
            column={1}
            size="small"
            labelStyle={{ fontWeight: 600, color: "#888", fontSize: 12 }}
            contentStyle={{ fontSize: 13 }}
          >
            <Descriptions.Item label="Type">{typeLabel} Link</Descriptions.Item>
            <Descriptions.Item label="File">{resultFileName}</Descriptions.Item>
            <Descriptions.Item label="URL">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="office-qr-link"
              >
                {downloadUrl}
              </a>
            </Descriptions.Item>
          </Descriptions>
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="office-qr-download-btn"
            onClick={handleDownloadQR}
          >
            Download QR PNG
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default OfficePdfResultActions;
